import EmployeeStatusRepository from '@api/repositories/employee-status-repository'
import { IEmployeeStatus } from '@app/interfaces/employee/employee-status'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import {
  createEmployeeStatus,
  updateEmployeeStatus,
} from '@app/store/actions/employee-status-actions'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from 'antd'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

interface props {
  employeeStatus?: IEmployeeStatus
  edit?: boolean
}

const EmployeeStatusForm: React.FC<props> = ({ employeeStatus, edit }) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()

  const employeeStatusTypes = ['WORKING']

  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IEmployeeStatus = {
      id: values.id,
      sorting: values.sorting,
      status: values.status,
      data: languages,
      type: values.type,
    }
    setConfirmLoading(true)
    const employeeStatusSubmitApi = employeeStatus
      ? EmployeeStatusRepository.update
      : EmployeeStatusRepository.create

    await employeeStatusSubmitApi(newValues)
      .then((response: AxiosResponse<IEmployeeStatus>) => {
        if (employeeStatus && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateEmployeeStatus(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!employeeStatus && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createEmployeeStatus(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={edit ? t('employeeStatus.edit') : t('employeeStatus.add')}
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: employeeStatus?.id,
              sorting: employeeStatus?.sorting || 0,
              status: edit ? employeeStatus?.status : true,
              type: employeeStatus?.type,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={employeeStatus?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label={t('employeeStatus.types.title')} name={'type'}>
              <Select disabled={confirmLoading}>
                {employeeStatusTypes.map((employeeStatusType, index) => {
                  return (
                    <Select.Option key={index} value={employeeStatusType}>
                      {t(`employeeStatus.types.${employeeStatusType}`)}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
            <FormList confirmLoading={confirmLoading} object={employeeStatus} />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default EmployeeStatusForm
